import React, { useMemo } from 'react';

/**
 * SloganLetterHead
 * Word-safe line breaking:
 * - We try to break at or before the target length, but never inside a word.
 * - If no whitespace exists before the target, we look ahead to the next whitespace.
 * - As a last resort (no whitespace at all), we hard-break to avoid an infinite loop.
 */

const NAME_BREAKS = [28, 28, 28];
const ADDRESS_BREAKS = [36, 36, 36];

/** Return the last whitespace index in s within [start, end] (inclusive of end-1). */
const lastWhitespaceBefore = (s, start, end) => {
  for (let i = Math.min(end - 1, s.length - 1); i >= start; i--) {
    if (/\s/.test(s[i])) return i;
  }
  return -1;
};

/** Return the first whitespace index in s at or after idx; -1 if none. */
const firstWhitespaceAfter = (s, idx) => {
  for (let i = idx; i < s.length; i++) {
    if (/\s/.test(s[i])) return i;
  }
  return -1;
};

/**
 * Word-safe split respecting target segment lengths.
 * We will never split in the middle of a word — we push the entire word to the next line.
 */
const splitAtPointsWordSafe = (text = '', points = []) => {
  const s = String(text || '').trim();
  if (!s) return [];

  const out = [];
  let start = 0;

  for (let i = 0; i < points.length && start < s.length; i++) {
    const targetLen = points[i];
    let end = Math.min(start + targetLen, s.length);

    if (end >= s.length) {
      // remainder fits entirely
      out.push(s.slice(start));
      start = s.length;
      break;
    }

    // Prefer a break BEFORE 'end' at a whitespace
    let breakAt = lastWhitespaceBefore(s, start, end);
    if (breakAt >= start) {
      out.push(s.slice(start, breakAt).trimEnd());
      start = breakAt + 1; // skip the space
      continue;
    }

    // No whitespace before target → look ahead AFTER 'end'
    const nextWs = firstWhitespaceAfter(s, end);
    if (nextWs !== -1) {
      out.push(s.slice(start, nextWs).trimEnd());
      start = nextWs + 1; // skip the space
      continue;
    }

    // No whitespace at all → hard break (single very-long token)
    out.push(s.slice(start, end));
    start = end;
  }

  // Any remainder after all points
  if (start < s.length) out.push(s.slice(start));

  return out.filter(Boolean);
};

const Lines = ({ lines, strongFirst = false }) => (
  <>
    {lines.map((ln, i) => (
      <span
        key={i}
        style={{
          ...(strongFirst && i === 0 ? { fontWeight: 700 } : {}),
          whiteSpace: 'pre-wrap',
          wordBreak: 'keep-all',       // don’t break inside words
          overflowWrap: 'normal',      // don’t break long words either (we manage lines)
        }}
      >
        {ln}
        {i < lines.length - 1 ? <br /> : null}
      </span>
    ))}
  </>
);

const cellBase = {
  padding: '6px 8px',
  verticalAlign: 'top',
  fontSize: '13px',
  fontFamily: 'Arial, Helvetica, sans-serif',
  color: '#000',
  lineHeight: 1.4,
};

const SloganLetterHead = ({ company = {}, title }) => {
  const {
    client_name,
    address,
    website,
    email_address,
    badge,
    motto,
  } = company || {};

  const nameLines = useMemo(
    () => splitAtPointsWordSafe(client_name, NAME_BREAKS),
    [client_name]
  );
  const addressLines = useMemo(
    () => splitAtPointsWordSafe(address, ADDRESS_BREAKS),
    [address]
  );

  return (
    <div className="content-wrapper d-flex" style={{ width: '100%' }}>
      <table
        width="100%"
        style={{ borderCollapse: 'collapse', borderBottom: '1px solid #000', marginBottom: 8 }}
        aria-label="Letterhead"
        border="0"
        className="no-table"
      >
        <tbody>
          <tr>
            {/* Left: Company Name (bold) + Motto directly beneath */}
            <td style={{ ...cellBase, width: '35%' }}>
              {nameLines.length ? (
                <div style={{ fontSize: 14 }}>
                  <Lines lines={nameLines} strongFirst />
                </div>
              ) : (
                <strong>{client_name || '—'}</strong>
              )}

              {motto ? (
                <div style={{ marginTop: 4, fontStyle: 'italic', fontSize: 12, color: '#333' }}>
                  {motto}
                </div>
              ) : null}
            </td>

            {/* Middle: Logo (centered) */}
            <td style={{ ...cellBase, width: '30%', textAlign: 'center' }}>
              {badge ? (
                <img
                  src={badge}
                  alt={`${client_name || 'Company'} logo`}
                  style={{ maxHeight: 70, maxWidth: '100%', objectFit: 'contain' }}
                />
              ) : (
                <span className="text-muted small">No Logo</span>
              )}
            </td>

            {/* Right: Address (word-safe broken) + contacts */}
            <td style={{ ...cellBase, width: '35%', textAlign: 'right' }}>
              {addressLines.length ? <Lines lines={addressLines} /> : null}
              {(website || email_address) && (
                <>
                  <br />
                  {website && (
                    <span title="Website" style={{ whiteSpace: 'nowrap' }}>
                      &#x1F310;&nbsp;{website}
                    </span>
                  )}
                  {website && email_address ? <span>&nbsp;&nbsp;&nbsp;</span> : null}
                  {email_address && (
                    <span title="Email" style={{ whiteSpace: 'nowrap' }}>
                      &#x2709;&nbsp;{email_address}
                    </span>
                  )}
                </>
              )}
            </td>
          </tr>
        </tbody>
      </table>

      {/* Title below the rule */}
      {title && (
        <div
          style={{
            width: '100%',
            textAlign: 'center',
            fontSize: 12,
            marginTop: 5,
            marginBottom: 20,
            color: '#000',
            fontWeight: 'bold',
          }}
        >
          {title}
        </div>
      )}
    </div>
  );
};

export default SloganLetterHead;
